import { MutationCache, QueryClient } from "@tanstack/react-query";

/** Retry once, only for network failures (status null) and gateway errors 502/503/504.
 *  Never for 500 (the legacy axios interceptor already toasts "Server error" on 500, so a retry would toast twice),
 *  never for 4xx (validation, permission, plan; 401/402/403 also redirect in the interceptor). */
const RETRYABLE = new Set([502, 503, 504]);
export const shouldRetry = (failureCount: number, error: unknown): boolean => {
  const status = (error as { status?: number | null })?.status ?? null;
  if (status !== null && !RETRYABLE.has(status)) return false;
  return failureCount < 1;
};

type MutationErrorHandler = (error: unknown) => void;
let onMutationError: MutationErrorHandler | null = null;
/** The shell registers one handler for every failed change in the app (the "blocked while viewing as" toast, 5A F3); null removes it. */
export const setMutationErrorHandler = (fn: MutationErrorHandler | null): void => { onMutationError = fn; };
/** A change made outside react-query (today's calls) reports its failure to the same handler. */
export const reportChangeError = (error: unknown): void => { onMutationError?.(error); };

export const createQueryClient = () =>
  new QueryClient({
    mutationCache: new MutationCache({ onError: (error) => onMutationError?.(error) }),
    defaultOptions: {
      queries: { staleTime: 30_000, refetchOnWindowFocus: true, retry: shouldRetry },
      mutations: { retry: false },
    },
  });
